import { Box } from "@outschool/backpack";
import {
  DefaultVariants,
  ExperimentVariants,
} from "@outschool/experiments-shared";
import { useOnScreen } from "@outschool/ui-utils";
import React, { useEffect, useRef } from "react";

import useExperiment from "../../hooks/useExperiment";
import { NoInfer } from "./NoInfer";

export const TriggerBehavior = {
  /**
   * Trigger the experiment when the component is rendered.
   * This is the default behavior and the most robust approach with regard to sample ratios and trigger consistency.
   * However, it's slightly less statistically powerful than ON_VIEW because it may trigger before the user actually sees the component
   * (if the component is initially off-screen). If this distinction is likely to cause a significant loss in statistical power,
   * you may want to use ON_VIEW, if you can be confident in doing so without introducing a sample ratio mismatch.
   */
  ON_RENDER: "on_render",
  /**
   * Trigger the experiment when the component is in the viewport.
   * This is a more statistically powerful approach because it only triggers after the user has seen the component.
   * If the user is likely to have to scroll to reach your component, this may be the right trigger behavior.
   * However, there be dragons here:
   * - For example, if your desired behavior is treatment: show a UI element, control: show nothing, it's hard to correctly trigger
   *   when the experiment element *would have been seen* by the user. By default, we use a tracking pixel for the null case, but that pixel
   *   may be seen at slightly different rates for the treatment and control groups, leading to a potentially biased sample. When using this behavior,
   *   you may want to manually add an invisible version of your treatment component to ensure that the treatment groups are seen at the same rate.
   *   Similarly, if your control and treatment displays are dramatically different sizes or shapes, you should think carefully about how to trigger.
   */
  ON_VIEW: "on_view",
};
export type TriggerBehavior =
  (typeof TriggerBehavior)[keyof typeof TriggerBehavior];

interface ExperimentTriggerWrapperProps<
  Variants extends ExperimentVariants = DefaultVariants,
> {
  /* The name of the experiment to trigger, should match the name in experiments admin. */
  name: string;
  /* Default variant to use if the experiment is not assigned. */
  defaultVariant?: NoInfer<Variants>;
  /* Child nodes to wrap. */
  children: React.ReactNode;
  /* Whether to disable triggering the experiment when the component is in the viewport. */
  disable?: boolean;
  /* Trigger behavior (see enum for more details) */
  triggerBehavior?: TriggerBehavior;
}

/**
 * Wraps a component used in the given experiment and triggers that experiment when that component enters the viewport.
 */
export default function ExperimentTriggerWrapper<
  Variants extends ExperimentVariants = DefaultVariants,
>({
  name,
  defaultVariant,
  disable = false,
  children,
  triggerBehavior = TriggerBehavior.ON_RENDER,
}: ExperimentTriggerWrapperProps<Variants>) {
  const { trigger } = useExperiment<Variants>(name, defaultVariant);

  const nodeRef = useRef<HTMLDivElement>(null);

  const isOnScreen = useOnScreen(nodeRef.current);

  const shouldTrigger =
    !disable &&
    (triggerBehavior === TriggerBehavior.ON_RENDER ||
      (triggerBehavior === TriggerBehavior.ON_VIEW && isOnScreen));

  useEffect(() => {
    if (shouldTrigger) {
      trigger();
    }
  }, [shouldTrigger, trigger]);

  return (
    // Need to wrap in a Box in order to correctly observe the component for useOnScreen.
    // Note: we may be able to remove this and use a cloning approach once we upgrade to React 19
    // See https://gitlab.com/outschool-eng/outschool/-/merge_requests/33366#note_2981683919
    <Box
      ref={nodeRef}
      sx={{
        ...(children === null
          ? { height: "1px", width: "1px", position: "absolute" }
          : {}),
      }}
    >
      {children}
    </Box>
  );
}
